// Location and shipping utilities
export const locationUtils = {
  // Kenyan counties (deduplicated and sorted alphabetically)
  counties: (() => {
    const allCounties = [
      'Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret', 'Thika', 'Malindi', 'Kitale',
      'Garissa', 'Kakamega', 'Nyeri', 'Meru', 'Machakos', 'Kitui', 'Embu', 'Nanyuki',
      'Kericho', 'Bungoma', 'Busia', 'Siaya', 'Kisii', 'Nyamira', 'Migori', 'Homa Bay',
      'Vihiga', 'Bomet', 'Narok', 'Kajiado', 'Makueni', 'Taita Taveta', 'Tana River',
      'Lamu', 'Kilifi', 'Kwale', 'Tana River', 'Isiolo', 'Marsabit', 'Samburu',
      'Turkana', 'West Pokot', 'Trans Nzoia', 'Uasin Gishu', 'Elgeyo Marakwet',
      'Nandi', 'Baringo', 'Laikipia', 'Nakuru', 'Narok', 'Kajiado', 'Kericho',
      'Bomet', 'Kakamega', 'Vihiga', 'Bungoma', 'Busia', 'Siaya', 'Kisumu',
      'Homa Bay', 'Migori', 'Kisii', 'Nyamira', 'Nairobi'
    ];
    // Remove duplicates and sort alphabetically
    return [...new Set(allCounties)].sort();
  })(),

  // Countries
  countries: [
    'Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Burundi', 'South Sudan', 'Ethiopia',
    'Somalia', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany',
    'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Switzerland', 'Sweden',
    'Norway', 'Denmark', 'Finland', 'Japan', 'South Korea', 'China', 'India',
    'South Africa', 'Nigeria', 'Ghana', 'Egypt', 'Morocco', 'Other'
  ],

  // Shipping cost is server-authoritative — see POST /api/cart/calculate-totals
  // (backend/src/utils/orderPricing.js). Don't recompute it here; a second,
  // independent table drifts from the real one and the numbers stop matching.

  // Get user's current location with fallback strategies
  getCurrentLocation: () => {
    return new Promise((resolve, reject) => {
      console.log('🔍 Checking geolocation support...');
      
      if (!navigator.geolocation) {
        const error = new Error('Geolocation is not supported by this browser');
        console.error('❌ Geolocation not supported');
        reject(error);
        return;
      }

      // Strategy 1: Try with cached location first (fastest)
      const quickOptions = {
        enableHighAccuracy: false,
        timeout: 5000, // 5 seconds for quick attempt
        maximumAge: 300000 // 5 minutes - use cached if available
      };

      console.log('✅ Geolocation API available, requesting position...');
      console.log('📍 Quick attempt with cached location (5s timeout)...');

      // First attempt: Quick with cached location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('✅ Position received (cached/fast):', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
            age: Date.now() - position.timestamp
          });
          
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (quickError) => {
          console.log('⚠️ Quick attempt failed, trying with longer timeout...');
          console.log('   Error:', quickError.code, quickError.message);

          // Strategy 2: Longer timeout for fresh location
          const fullOptions = {
            enableHighAccuracy: false, // Network-based (faster than GPS)
            timeout: 15000, // 15 seconds - reasonable timeout
            maximumAge: 0 // Get fresh location
          };

          console.log('📍 Full attempt with fresh location (15s timeout)...');
          console.log('📍 Options:', {
            enableHighAccuracy: fullOptions.enableHighAccuracy,
            timeout: `${fullOptions.timeout}ms`,
            maximumAge: `${fullOptions.maximumAge}ms`
          });

          navigator.geolocation.getCurrentPosition(
            (position) => {
              console.log('✅ Position received (fresh):', {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                timestamp: position.timestamp
              });
              
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy
              });
            },
            (error) => {
              // Log detailed error information
              console.error('❌ Geolocation error (both attempts failed):', {
                code: error.code,
                message: error.message,
                PERMISSION_DENIED: error.PERMISSION_DENIED,
                POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
                TIMEOUT: error.TIMEOUT,
                quickErrorCode: quickError.code
              });

              // Provide more descriptive error messages
              let errorMessage = 'Failed to get location';
              let errorCode = error.code;
              
              if (error.code === error.PERMISSION_DENIED || error.code === 1) {
                errorMessage = 'Location permission denied by user';
                errorCode = 1;
                console.error('❌ Error Code 1: PERMISSION_DENIED');
              } else if (error.code === error.POSITION_UNAVAILABLE || error.code === 2) {
                errorMessage = 'Location information is unavailable. This may be due to:\n- GPS is disabled\n- Poor network connection\n- Location services are unavailable\n- Browser location service error\n\nPlease try:\n1. Enable location services on your device\n2. Check your internet connection\n3. Try again in a few moments\n4. Or enter your address manually';
                errorCode = 2;
                console.error('❌ Error Code 2: POSITION_UNAVAILABLE - GPS/Network issue');
              } else if (error.code === error.TIMEOUT || error.code === 3) {
                errorMessage = 'Location request timed out. The device took too long to determine your location.\n\nThis can happen when:\n- Network connection is slow\n- Location services are initializing\n- GPS signal is weak\n\nPlease try:\n1. Wait a moment and try again\n2. Move to an area with better signal\n3. Check your internet connection\n4. Or enter your address manually';
                errorCode = 3;
                console.error('❌ Error Code 3: TIMEOUT - Request took longer than 15 seconds');
              } else {
                errorMessage = error.message || 'Unknown geolocation error';
                console.error('❌ Unknown error code:', error.code);
              }
              
              const enhancedError = new Error(errorMessage);
              enhancedError.code = errorCode;
              enhancedError.originalError = error;
              enhancedError.quickError = quickError;
              console.error('❌ Rejecting with error:', enhancedError);
              reject(enhancedError);
            },
            fullOptions
          );
        },
        quickOptions
      );
    });
  },

  // Format phone number for Kenya
  formatPhoneNumber: (phone) => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Convert to Kenyan format
    if (cleaned.startsWith('254')) {
      return `+${cleaned}`;
    } else if (cleaned.startsWith('0')) {
      return `+254${cleaned.substring(1)}`;
    } else if (cleaned.length === 9) {
      return `+254${cleaned}`;
    }
    
    return phone; // Return original if can't format
  }
};

